import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { generateUrlForWorkspace } from '../common';
import Fuse from 'fuse.js';
import apiFetch from '../../util/fetch-utils';
import { toaster } from 'evergreen-ui';
import { WORKSPACE_CREATED_EVENT } from '../../../actions/workspaces';
import styled from 'styled-components';
import {
    Grid,
    Typography,
    InputBase,
    Button,
    IconButton,
    Card,
    CardContent,
    ButtonGroup,
} from '@material-ui/core';
import { AddCircle, WorkOutline, SortByAlpha } from '@material-ui/icons';
import SearchIcon from '@material-ui/icons/Search';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import Skeleton from '@material-ui/lab/Skeleton';
import WorkspaceCard from './WorkspaceCard';
import { inCriticalityOrder, useCriticalityCounts } from './criticality';

const MAX_WORKSPACE_PER_PAGE = 16;

const PageHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
    @media (max-width: 600px) { padding: 0 16px; }
`;

const TitleBlock = styled.div``;

const PageTitle = styled(Typography)`
    font-weight: 700 !important;
    letter-spacing: -0.5px !important;
`;

const SubTitle = styled(Typography)`
    color: #6b7280;
    margin-top: 2px !important;
`;

const AddButton = styled(Button)`
    && {
        background: linear-gradient(135deg, #6366f1, #8b5cf6);
        color: #fff;
        font-weight: 600;
        border-radius: 10px;
        padding: 10px 20px;
        text-transform: none;
        font-size: 0.9rem;
        box-shadow: 0 4px 14px rgba(99,102,241,.3);
        white-space: nowrap;
    }
    &&:hover {
        background: linear-gradient(135deg, #4f46e5, #7c3aed);
        box-shadow: 0 6px 20px rgba(99,102,241,.4);
    }
    &&:disabled {
        background: #e5e7eb;
        color: #9ca3af;
        box-shadow: none;
    }
`;

const ToolbarRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    @media (max-width: 600px) { padding: 0 16px; }
`;

const SearchContainer = styled.div`
    padding: 2px 4px;
    display: flex;
    align-items: center;
    flex: 1 1 280px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
`;

const StyledInput = styled(InputBase)`
    margin-left: 0.5rem;
    flex: 1;
    font-size: 0.9rem !important;
`;

const SortButton = styled(Button)`
    && {
        border-radius: 12px;
        text-transform: none;
        font-size: 0.85rem;
        color: #374151;
        border-color: #e2e8f0;
        white-space: nowrap;
        padding: 7px 14px;
    }
`;

const StatusBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 14px 0 4px;
    flex-wrap: wrap;
    gap: 8px;
    @media (max-width: 600px) { padding: 0 16px; }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
    gap: 12px;
    color: #9ca3af;
`;

const SkeletonCard = styled(Card)`
    border-radius: 12px !important;
    overflow: hidden;
    border: 1px solid #e2e8f0 !important;
    box-shadow: none !important;
`;

const CardGrid = styled(Grid)`
    @media (max-width: 600px) {
        margin-left: 0 !important;
        margin-right: 0 !important;
        width: 100% !important;
        & > .MuiGrid-item {
            padding-left: 0 !important;
            padding-right: 0 !important;
        }
    }
`;

const ShowMoreButton = styled(Button)`
    && {
        border-radius: 12px;
        text-transform: none;
        font-size: 0.875rem;
        border-color: #e2e8f0;
        color: #374151;
        padding: 8px 24px;
    }
    &&:hover {
        border-color: #6366f1;
        color: #6366f1;
    }
`;

const WorkspaceSelectionPage = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const workspaces = useSelector((state) => state.workspaces);
    const issueCounts = useCriticalityCounts();
    const [filter, setFilter] = useState('');
    const [creating, setCreating] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [sortDir, setSortDir] = useState(null); // null | 'asc' | 'desc'

    const createWorkspace = () => {
        if (creating) return;
        setCreating(true);
        apiFetch('/api/workspaces', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.status === 201) {
                    response.json().then((responseJson) => {
                        dispatch({ type: WORKSPACE_CREATED_EVENT, payload: responseJson });
                        history.push(generateUrlForWorkspace(responseJson.seqNumber) + '/admin');
                    });
                } else {
                    toaster.danger('Unable to create workspace.', {
                        description: response.status + ' ' + response.statusText,
                        duration: 10,
                    });
                    setCreating(false);
                }
            })
            .catch(() => {
                toaster.danger('Unable to create workspace.');
                setCreating(false);
            });
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
        setShowAll(false);
    };

    const cycleSort = () => {
        setSortDir((d) => (d === null ? 'asc' : d === 'asc' ? 'desc' : null));
    };

    const isLoading = !workspaces?.fetched;
    const all = Array.isArray(workspaces?.data) ? workspaces.data : [];

    const filteredWorkspaces = useMemo(() => {
        let result = all;
        if (filter && all.length) {
            const fuse = new Fuse(all, {
                shouldSort: true,
                includeScore: true,
                threshold: 0.3,
                distance: 100,
                ignoreLocation: true,
                minMatchCharLength: 1,
                keys: ['name', 'websiteUrl'],
            });
            result = fuse.search(filter).map((r) => r.item);
        }
        if (sortDir) {
            return [...result].sort((a, b) => {
                const na = (a.name || '').toLowerCase();
                const nb = (b.name || '').toLowerCase();
                return sortDir === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
            });
        }
        return issueCounts ? inCriticalityOrder(result, issueCounts) : result;
    }, [all, filter, sortDir, issueCounts]);

    const total = filteredWorkspaces.length;
    const hasMore = !showAll && total > MAX_WORKSPACE_PER_PAGE;
    const visible = showAll ? filteredWorkspaces : filteredWorkspaces.slice(0, MAX_WORKSPACE_PER_PAGE);

    const SortIcon = sortDir === 'desc' ? ArrowDownwardIcon : ArrowUpwardIcon;
    const sortLabel =
        sortDir === 'asc' ? 'A → Z' : sortDir === 'desc' ? 'Z → A' : 'Sort';

    return (
        <div>
            <PageHeader>
                <TitleBlock>
                    <PageTitle variant="h5" component="h1">
                        Workspaces
                    </PageTitle>
                    {!isLoading && (
                        <SubTitle variant="body2">
                            {all.length} workspace{all.length !== 1 ? 's' : ''} in your account
                        </SubTitle>
                    )}
                </TitleBlock>

                <AddButton
                    variant="contained"
                    onClick={createWorkspace}
                    disabled={creating}
                    startIcon={<AddCircle />}
                >
                    {creating ? 'Creating…' : 'New Workspace'}
                </AddButton>
            </PageHeader>

            <ToolbarRow>
                <SearchContainer elevation={0}>
                    <IconButton aria-label="search" disabled size="small">
                        <SearchIcon />
                    </IconButton>
                    <StyledInput
                        type="search"
                        name="filter"
                        placeholder="Search by name or URL…"
                        value={filter}
                        onChange={handleFilterChange}
                        inputProps={{ 'aria-label': 'search workspaces' }}
                    />
                </SearchContainer>

                <SortButton
                    variant="outlined"
                    onClick={cycleSort}
                    startIcon={sortDir ? <SortIcon style={{ fontSize: 16 }} /> : <SortByAlpha style={{ fontSize: 16 }} />}
                >
                    {sortLabel}
                </SortButton>
            </ToolbarRow>

            <StatusBar>
                <Typography variant="body2" color="textSecondary">
                    {isLoading ? (
                        'Fetching workspaces…'
                    ) : (
                        <>
                            Showing <b>{visible.length}</b> of <b>{total}</b>
                            {filter ? <> matching "{filter}"</> : null}
                        </>
                    )}
                </Typography>
            </StatusBar>

            <CardGrid container spacing={3} style={{ marginTop: 4 }}>
                {isLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <SkeletonCard>
                                <Skeleton variant="rect" height={4} />
                                <CardContent style={{ padding: 16 }}>
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                        <Skeleton variant="circle" width={52} height={52} />
                                        <div style={{ flex: 1 }}>
                                            <Skeleton variant="text" width="65%" height={22} />
                                            <Skeleton variant="text" width="45%" height={16} />
                                        </div>
                                    </div>
                                    <Skeleton variant="rect" width={80} height={22} style={{ borderRadius: 6, marginTop: 12 }} />
                                </CardContent>
                                <CardContent style={{ padding: '10px 16px 14px', display: 'flex', gap: 8 }}>
                                    <Skeleton variant="rect" width={90} height={34} style={{ borderRadius: 8 }} />
                                    <Skeleton variant="circle" width={34} height={34} style={{ marginLeft: 'auto' }} />
                                </CardContent>
                            </SkeletonCard>
                        </Grid>
                    ))
                ) : total === 0 ? (
                    <Grid item xs={12}>
                        <EmptyState>
                            <WorkOutline style={{ fontSize: 64 }} />
                            <Typography variant="h6" style={{ color: '#6b7280', fontWeight: 600 }}>
                                {filter ? `No workspaces match "${filter}"` : 'No workspaces yet'}
                            </Typography>
                            {!filter && (
                                <Typography variant="body2" style={{ color: '#9ca3af' }}>
                                    Create your first workspace to get started.
                                </Typography>
                            )}
                        </EmptyState>
                    </Grid>
                ) : (
                    visible.map((curWs, index) => (
                        <WorkspaceCard key={curWs.seqNumber ?? index} workspace={curWs} loadingIssues={!issueCounts} issueCounts={issueCounts?.[curWs.seqNumber]} />
                    ))
                )}
            </CardGrid>

            {hasMore && (
                <div style={{ marginTop: 28, textAlign: 'center' }}>
                    <ShowMoreButton variant="outlined" onClick={() => setShowAll(true)}>
                        Show all {total} workspaces
                    </ShowMoreButton>
                </div>
            )}
        </div>
    );
};

export default WorkspaceSelectionPage;
